import { useEffect, useRef, useState } from 'react'
import { useAsync } from '../hooks/useAsync'
import { errorMessage } from '../services/api'
import {
  archivePoll,
  closePoll,
  fetchAdminPoll,
  publishPoll,
  updatePoll,
} from '../services/league'
import { Card, EmptyState, Loading, Notice } from './ui'
import type { Poll, PollDurationUnit, PollOptionDraft } from '../types'
import './PollPanel.css'

interface DraftOption extends PollOptionDraft {
  /** Stable across drags so a reorder does not remount the text field. */
  key: string
}

function toDraftOptions(poll: Poll): DraftOption[] {
  return poll.options.map(option => ({
    id: option.id,
    label: option.label,
    key: `id-${option.id}`,
  }))
}

function emptyOption(key: string): DraftOption {
  return { key, label: '' }
}

function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) {
    return list
  }
  const next = [...list]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  return next
}

function durationFromNow(closesAt: string): { value: string; unit: PollDurationUnit } {
  const remainingMs = new Date(closesAt).getTime() - Date.now()
  if (remainingMs <= 0) return { value: '1', unit: 'days' }
  const hours = Math.max(1, Math.round(remainingMs / (60 * 60 * 1000)))
  if (hours % 24 === 0 && hours >= 24) {
    return { value: String(hours / 24), unit: 'days' }
  }
  return { value: String(hours), unit: 'hours' }
}

function formatClosesAt(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Admin control for the homepage poll: question, options, and how long voting
 * stays open. Tallies only — never a voter list.
 */
export default function PollPanel() {
  const loaded = useAsync(fetchAdminPoll, [])
  const [poll, setPoll] = useState<Poll | null>(null)
  const [question, setQuestion] = useState('')
  const [options, setOptions] = useState<DraftOption[]>(() => [
    emptyOption('new-1'),
    emptyOption('new-2'),
  ])
  const [durationValue, setDurationValue] = useState('7')
  const [durationUnit, setDurationUnit] = useState<PollDurationUnit>('days')
  const [extendOnSave, setExtendOnSave] = useState(false)
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState<{ tone: 'success' | 'error'; text: string } | null>(null)
  const [creating, setCreating] = useState(false)
  const nextKey = useRef(3)

  useEffect(() => {
    if (loaded.loading) return

    const next = loaded.data
    setPoll(next)
    if (next) {
      setQuestion(next.question)
      setOptions(toDraftOptions(next))
      const remaining = durationFromNow(next.closesAt)
      setDurationValue(remaining.value)
      setDurationUnit(remaining.unit)
      setExtendOnSave(false)
      setCreating(false)
    } else {
      setQuestion('')
      setOptions([emptyOption('new-1'), emptyOption('new-2')])
      nextKey.current = 3
      setDurationValue('7')
      setDurationUnit('days')
      setCreating(true)
    }
  }, [loaded.data, loaded.loading])

  function setOptionLabel(index: number, label: string): void {
    setOptions(current => current.map((option, i) => (i === index ? { ...option, label } : option)))
  }

  function addOption(): void {
    setOptions(current => {
      if (current.length >= 8) return current
      const key = `new-${nextKey.current}`
      nextKey.current += 1
      return [...current, emptyOption(key)]
    })
  }

  function removeOption(index: number): void {
    setOptions(current => (current.length <= 2 ? current : current.filter((_, i) => i !== index)))
  }

  function reorderOptions(from: number, to: number): void {
    setOptions(current => moveItem(current, from, to))
  }

  const labels = options.map(option => option.label.trim()).filter(Boolean)
  const duration = Number(durationValue)
  const durationOk = Number.isInteger(duration) && duration > 0
  const canPublish = question.trim().length > 0 && labels.length >= 2 && durationOk && !busy

  async function run(action: () => Promise<unknown>, success: string): Promise<void> {
    setBusy(true)
    setStatus(null)
    try {
      await action()
      setStatus({ tone: 'success', text: success })
      loaded.reload()
    } catch (caught) {
      setStatus({ tone: 'error', text: errorMessage(caught) })
    } finally {
      setBusy(false)
    }
  }

  async function publish(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    await run(
      () =>
        publishPoll({
          question: question.trim(),
          options: labels,
          openFor: { value: duration, unit: durationUnit },
        }),
      'Poll is live on the homepage.'
    )
  }

  async function saveEdits(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    if (!poll) return
    await run(async () => {
      const next = await updatePoll({
        question: question.trim(),
        options: options
          .map(option => ({ id: option.id, label: option.label.trim() }))
          .filter(option => option.label.length > 0),
        openFor: extendOnSave ? { value: duration, unit: durationUnit } : undefined,
      })
      setPoll(next)
    }, 'Poll updated.')
  }

  const showCreate = creating || !poll

  return (
    <div className="stack">
      {status && <Notice tone={status.tone}>{status.text}</Notice>}

      {loaded.loading ? (
        <Card title="Homepage poll">
          <Loading />
        </Card>
      ) : (
        <>
          {poll && !creating && (
            <Card
              title="Current poll"
              subtitle={poll.isOpen ? `Open until ${formatClosesAt(poll.closesAt)}` : 'Voting closed'}
            >
              <p className="muted poll-admin__tally">
                {poll.totalVotes} {poll.totalVotes === 1 ? 'vote' : 'votes'} recorded. Counts only —
                individual ballots are never shown.
              </p>
              <ul className="poll-admin__counts">
                {poll.options.map(option => (
                  <li key={option.id}>
                    <span>{option.label}</span>
                    <span className="numeric">{option.votes}</span>
                  </li>
                ))}
              </ul>
              <div className="row">
                <button
                  type="button"
                  className="btn"
                  disabled={busy || !poll.isOpen}
                  onClick={() =>
                    void run(() => closePoll(), 'Voting is closed. Results stay on the homepage.')
                  }
                >
                  Close voting now
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() => void run(() => archivePoll(), 'Poll removed from the homepage.')}
                >
                  Remove from homepage
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  disabled={busy}
                  onClick={() => {
                    setCreating(true)
                    setQuestion('')
                    setOptions([emptyOption('new-1'), emptyOption('new-2')])
                    nextKey.current = 3
                    setDurationValue('7')
                    setDurationUnit('days')
                  }}
                >
                  Replace with a new poll
                </button>
              </div>
            </Card>
          )}

          {poll && !creating && (
            <Card
              title="Edit poll"
              subtitle="Changing the question or option wording keeps existing votes"
            >
              <form className="stack" onSubmit={event => void saveEdits(event)}>
                <PollFields
                  question={question}
                  options={options}
                  durationValue={durationValue}
                  durationUnit={durationUnit}
                  durationHint="Leave this unless you want to restart the clock from now."
                  showDurationToggle
                  extendOnSave={extendOnSave}
                  onExtendOnSave={setExtendOnSave}
                  onQuestion={setQuestion}
                  onOptionLabel={setOptionLabel}
                  onAddOption={addOption}
                  onRemoveOption={removeOption}
                  onReorder={reorderOptions}
                  onDurationValue={setDurationValue}
                  onDurationUnit={setDurationUnit}
                />
                <div className="row">
                  <button type="submit" className="btn btn--primary" disabled={!canPublish}>
                    {busy ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </form>
            </Card>
          )}

          {showCreate && (
            <Card
              title={poll ? 'New homepage poll' : 'Homepage poll'}
              subtitle={
                poll
                  ? 'Publishing this takes the current poll down and starts a fresh vote'
                  : 'Shown under the buttons on the front page'
              }
            >
              {!poll && !loaded.loading && (
                <EmptyState title="No poll on the homepage">
                  Set a question, at least two options, and how long voting stays open.
                </EmptyState>
              )}
              <form className="stack" onSubmit={event => void publish(event)}>
                <PollFields
                  question={question}
                  options={options}
                  durationValue={durationValue}
                  durationUnit={durationUnit}
                  durationHint="Counted from the moment you publish."
                  onQuestion={setQuestion}
                  onOptionLabel={setOptionLabel}
                  onAddOption={addOption}
                  onRemoveOption={removeOption}
                  onReorder={reorderOptions}
                  onDurationValue={setDurationValue}
                  onDurationUnit={setDurationUnit}
                />
                <div className="row">
                  <button type="submit" className="btn btn--primary" disabled={!canPublish}>
                    {busy ? 'Publishing…' : poll ? 'Publish and replace' : 'Publish poll'}
                  </button>
                  {poll && (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      disabled={busy}
                      onClick={() => {
                        setCreating(false)
                        setQuestion(poll.question)
                        setOptions(toDraftOptions(poll))
                        const remaining = durationFromNow(poll.closesAt)
                        setDurationValue(remaining.value)
                        setDurationUnit(remaining.unit)
                        setExtendOnSave(false)
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

function PollFields({
  question,
  options,
  durationValue,
  durationUnit,
  durationHint,
  showDurationToggle = false,
  extendOnSave = false,
  onExtendOnSave,
  onQuestion,
  onOptionLabel,
  onAddOption,
  onRemoveOption,
  onReorder,
  onDurationValue,
  onDurationUnit,
}: {
  question: string
  options: DraftOption[]
  durationValue: string
  durationUnit: PollDurationUnit
  durationHint: string
  showDurationToggle?: boolean
  extendOnSave?: boolean
  onExtendOnSave?: (value: boolean) => void
  onQuestion: (value: string) => void
  onOptionLabel: (index: number, label: string) => void
  onAddOption: () => void
  onRemoveOption: (index: number) => void
  onReorder: (from: number, to: number) => void
  onDurationValue: (value: string) => void
  onDurationUnit: (value: PollDurationUnit) => void
}) {
  const listRef = useRef<HTMLOListElement>(null)
  const draggingFrom = useRef<number | null>(null)
  const [dragging, setDragging] = useState<number | null>(null)

  function indexAtY(clientY: number): number | null {
    const list = listRef.current
    if (!list) return null
    const rows = [...list.querySelectorAll<HTMLElement>('[data-option-index]')]
    if (rows.length === 0) return null

    let best = 0
    let bestDist = Number.POSITIVE_INFINITY
    for (const row of rows) {
      const rect = row.getBoundingClientRect()
      const mid = rect.top + rect.height / 2
      const dist = Math.abs(clientY - mid)
      const index = Number(row.dataset.optionIndex)
      if (!Number.isInteger(index)) continue
      if (dist < bestDist) {
        bestDist = dist
        best = index
      }
    }
    return best
  }

  function stopDragging(): void {
    draggingFrom.current = null
    setDragging(null)
  }

  function onHandlePointerDown(event: React.PointerEvent<HTMLButtonElement>, index: number): void {
    if (event.button !== 0) return
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    draggingFrom.current = index
    setDragging(index)
  }

  function onHandlePointerMove(event: React.PointerEvent<HTMLButtonElement>): void {
    const from = draggingFrom.current
    if (from === null) return
    const to = indexAtY(event.clientY)
    if (to === null || to === from) return
    onReorder(from, to)
    draggingFrom.current = to
    setDragging(to)
  }

  function onHandleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, index: number): void {
    if (event.key === 'ArrowUp' && index > 0) {
      event.preventDefault()
      onReorder(index, index - 1)
    } else if (event.key === 'ArrowDown' && index < options.length - 1) {
      event.preventDefault()
      onReorder(index, index + 1)
    }
  }

  return (
    <>
      <label className="field">
        <span className="field__label">Question</span>
        <input
          className="input"
          value={question}
          maxLength={280}
          placeholder="Who wins the AFC?"
          onChange={event => onQuestion(event.target.value)}
        />
      </label>

      <div className="field">
        <span className="field__label">Options</span>
        <ol ref={listRef} className="poll-admin__options">
          {options.map((option, index) => (
            <li
              key={option.key}
              data-option-index={index}
              className={dragging === index ? 'is-dragging' : undefined}
            >
              <button
                type="button"
                className="poll-admin__handle"
                aria-label={`Reorder option ${index + 1}`}
                title="Drag to reorder"
                onPointerDown={event => onHandlePointerDown(event, index)}
                onPointerMove={onHandlePointerMove}
                onPointerUp={stopDragging}
                onPointerCancel={stopDragging}
                onKeyDown={event => onHandleKeyDown(event, index)}
              >
                <span aria-hidden="true" />
                <span aria-hidden="true" />
                <span aria-hidden="true" />
              </button>
              <input
                className="input"
                value={option.label}
                maxLength={80}
                placeholder={`Option ${index + 1}`}
                onChange={event => onOptionLabel(index, event.target.value)}
              />
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={options.length <= 2}
                onClick={() => onRemoveOption(index)}
              >
                Remove
              </button>
            </li>
          ))}
        </ol>
        <span className="field__hint">Drag the handle to change the order the homepage shows.</span>
        <button
          type="button"
          className="btn btn--sm"
          disabled={options.length >= 8}
          onClick={onAddOption}
        >
          Add option
        </button>
      </div>

      <div className="poll-admin__duration">
        {showDurationToggle && onExtendOnSave && (
          <label className="poll-admin__extend">
            <input
              type="checkbox"
              checked={extendOnSave}
              onChange={event => onExtendOnSave(event.target.checked)}
            />
            Restart the clock from now
          </label>
        )}
        <label className="field">
          <span className="field__label">Open for</span>
          <span className="poll-admin__open-for">
            <input
              className="input"
              type="number"
              min={1}
              max={durationUnit === 'days' ? 90 : 2160}
              value={durationValue}
              disabled={showDurationToggle && !extendOnSave}
              onChange={event => onDurationValue(event.target.value)}
            />
            <select
              className="select"
              value={durationUnit}
              disabled={showDurationToggle && !extendOnSave}
              onChange={event => onDurationUnit(event.target.value as PollDurationUnit)}
            >
              <option value="hours">hours</option>
              <option value="days">days</option>
            </select>
          </span>
          <span className="field__hint">{durationHint}</span>
        </label>
      </div>
    </>
  )
}
